import React, { useState } from 'react';
import {
  ApiResponse,
  LoadItem,
  loadList,
  ItemDetailModal,
  ItemInventory,
} from "../../models/Data";
import { RestApi } from '../../services/Api';
import {
  IonButton,
  IonCardContent,
  IonContent,
  IonHeader,
  IonIcon,
  IonListHeader,
  IonPage,
  IonSkeletonText,
  IonThumbnail,
  IonToolbar,
  useIonAlert,
  IonCard,
  IonCardHeader,
  IonItem,
  IonText,
  IonList,
  IonLabel,
  IonRefresherContent,
  IonRefresher,
  RefresherEventDetail,
  IonBadge,
} from "@ionic/react";
import { getNameDate, useConstructor } from '../../services/Library';
import { useTranslation } from 'react-i18next';
import {
  arrowBackOutline,
  calendarClearOutline,
  documentOutline,
  chevronDownCircleOutline,
  documentTextOutline,
  fileTrayStackedOutline,
} from "ionicons/icons";
import "./Detail.scss";

const Detail: React.FC<ItemDetailModal> = (props) => {
  const [item, setItem] = useState<ItemInventory>();
  const [alert] = useIonAlert();
  const { t } = useTranslation(["general"]);
  const [loaded, setLoaded] = useState<boolean>(false);

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [{ cssClass: "alert-button-confirm", text: t("yes") }],
    });
  };

  const getItem = (id: string) => {
    if (!id) {
      props.onDismissDetail();
      return;
    }
    setLoaded(false);
    RestApi(`item/${id}`, "get", {}, "application/json")
      .then(async (res: ApiResponse) => {
        if (res.data.status) {
          setItem(res.data.data);
        } else {
          props.onDismissDetail();
        }
        setLoaded(true);
      })
      .catch((err: Error) => {
        setLoaded(true);
        massageAlert("error", err.message);
        props.onDismissDetail();
      });
  };

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    getItem(props.item_id);
    event.detail.complete();
  };

  useConstructor(() => {
    getItem(props.item_id);
  });

  return (
    <IonPage>
      <IonHeader className="ion-no-padding ion-no-margin toolbar-detail">
        {loaded && item ? (
          <IonToolbar class="ion-no-padding ion-no-margin">
            <IonButton
              color="light"
              slot="start"
              fill="clear"
              onClick={() => props.onDismissDetail()}
            >
              <IonIcon color="light" slot="start" src={arrowBackOutline} />
            </IonButton>
            <IonText color={"light"}>
              <p>
                <strong>
                  {item.item_name} ({item.item_id})
                </strong>
              </p>
            </IonText>
          </IonToolbar>
        ) : (
          <IonToolbar>
            <IonSkeletonText
              animated={true}
              style={{ width: "100%" }}
            ></IonSkeletonText>
            <IonSkeletonText
              animated={true}
              style={{ width: "100%" }}
            ></IonSkeletonText>
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={`${t("loading")}`}
            refreshingSpinner="circles"
            refreshingText={`${t("loading")}`}
          ></IonRefresherContent>
        </IonRefresher>
        {loaded && item ? (
          <IonCard>
            <IonCardHeader className="ion-no-padding ion-no-margin ion-padding-start ion-padding-top ion-padding-end">
              <IonItem className="ion-no-padding ion-no-margin">
                <IonText className="ion-text-bold ion-text-sm">
                  <IonBadge color={"medium"}>
                    {item.category?.category_name}
                  </IonBadge>
                  <p className="ion-no-padding ion-no-margin">
                    {item.category?.grouping?.grouping_name}
                  </p>
                </IonText>
                <IonText
                  className="ion-text-sm ion-text-bold ion-text-end"
                  slot="end"
                >
                  <IonBadge color="primary">{item.item_unit}</IonBadge>
                </IonText>
              </IonItem>
              <IonItem className="ion-no-padding ion-no-margin">
                <IonText>
                  <p className="ion-no-padding ion-no-margin">
                    <IonIcon
                      size="small"
                      color="dark"
                      src={fileTrayStackedOutline}
                    ></IonIcon>
                    <small>{` ${t("total")} ${t("in")} : ${
                      item.total_in
                    } `}</small>
                    <small>{`${t("total")} ${t("out")} : ${
                      item.total_out
                    } `}</small>
                    <small>{`${t("total")} ${t("stock")} : ${
                      item.total_stock
                    } `}</small>
                  </p>
                </IonText>
              </IonItem>
              <IonItem className="ion-no-padding ion-no-margin">
                <IonText className="ion-no-padding ion-no-margin">
                  <small className="ion-no-padding ion-no-margin">
                    {t("create")} : {item.item_create_by}
                  </small>
                  <br />
                  <small className="ion-no-padding ion-no-margin">
                    {getNameDate(item.item_create_at, true)}
                  </small>
                </IonText>
                <IonText
                  className="ion-no-padding ion-no-margin ion-text-end"
                  slot="end"
                >
                  <small className="ion-no-padding ion-no-margin">
                    {t("update")} : {item.item_update_by ?? ""}
                  </small>
                  <br />
                  <small className="ion-no-padding ion-no-margin">
                    {getNameDate(item.item_update_at, true)}
                  </small>
                </IonText>
              </IonItem>
            </IonCardHeader>
            <IonCardContent className="ion-no-padding ion-no-margin">
              <p className="ion-padding-top">
                <strong>
                  &nbsp;&nbsp;
                  {`${t("list")} ${t("item")} ${t("in")}/${t("out")}`}
                </strong>
              </p>
              <IonList>
                {item.inventorydet?.map((detail, index) => (
                  <IonCard
                    key={index}
                  >
                    <IonItem>
                      <IonText className="ion-text-bold ion-text-sm">
                        <p className="ion-no-padding ion-no-margin">
                          <IonIcon icon={calendarClearOutline} />
                          {" " + getNameDate(detail.inventory?.inventory_date)}
                        </p>
                        <p className="ion-no-padding ion-no-margin">
                          <IonIcon icon={documentOutline} />
                          {" " + detail.inventory?.inventory_form_no}
                        </p>
                      </IonText>
                      <IonText
                        className="ion-text-sm ion-text-bold ion-text-end"
                        slot="end"
                      >
                        <p className="ion-no-padding ion-no-margin">
                          <IonIcon icon={documentTextOutline} />

                          {t(
                            `${detail.inventory?.inventory_type_document.toLowerCase()}`
                          )}
                        </p>
                        <p className="ion-no-padding ion-no-margin">
                          {t(
                            detail.inventory?.inventory_type.toLowerCase() ?? ""
                          ) + " : "}
                          <IonBadge color={"secondary"}>
                            {detail.inventorydet_quantity}
                          </IonBadge>
                        </p>
                      </IonText>
                    </IonItem>
                    <IonItem>
                      <IonText className="ion-no-padding ion-no-margin">
                        <small className="ion-no-padding ion-no-margin">
                          {t("create")} :{" "}
                          {detail.inventory?.inventory_create_by}
                        </small>
                        <br />
                        <small className="ion-no-padding ion-no-margin">
                          {getNameDate(
                            detail.inventory?.inventory_create_at,
                            true
                          )}
                        </small>
                      </IonText>
                      <IonText
                        className="ion-no-padding ion-no-margin ion-text-end"
                        slot="end"
                      >
                        <small className="ion-no-padding ion-no-margin">
                          {t("update")} :{" "}
                          {detail.inventory?.inventory_update_by ?? ""}
                        </small>
                        <br />
                        <small className="ion-no-padding ion-no-margin">
                          {getNameDate(
                            detail.inventory?.inventory_update_at,
                            true
                          )}
                        </small>
                      </IonText>
                    </IonItem>
                  </IonCard>
                ))}
              </IonList>
            </IonCardContent>
          </IonCard>
        ) : (
          <IonList>
            <IonListHeader>
              <IonSkeletonText
                animated={true}
                style={{ width: "80px" }}
              ></IonSkeletonText>
            </IonListHeader>
            {loadList.map((value: LoadItem, key: number) => (
              <IonItem key={key}>
                <IonThumbnail slot="start">
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonThumbnail>
                <IonLabel>
                  <h3>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "80%" }}
                    ></IonSkeletonText>
                  </h3>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "60%" }}
                    ></IonSkeletonText>
                  </p>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "30%" }}
                    ></IonSkeletonText>
                  </p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Detail;
